/**
 * Cross-component signal for which view a mini-app editor tab should show —
 * the running app ("run") or the settings page ("settings").
 *
 * The `.miniapp` folder opens as a SINGLE multi-purpose tab (MiniAppEditor),
 * so the workspace panel-header "Run"/"Settings" buttons can't select the view
 * by opening a different tab — they must tell the (possibly already-mounted)
 * editor which face to show. Two channels cover both timings:
 *   - a pending map, read once by the editor's mount initializer (the tab was
 *     not open yet — openFile mounts it AFTER the button fires); and
 *   - a window event, for when the tab is already open (no remount).
 */
export type MiniAppView = 'run' | 'settings'

const EVENT = 'papan:miniapp-view'
const pending = new Map<string, MiniAppView>()

/** Ask the mini-app editor for `folderId` to show `view`. Call BEFORE openFile. */
export function requestMiniappView(folderId: string, view: MiniAppView): void {
  pending.set(folderId, view)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { folderId, view } }))
  }
}

/** Read + clear the pending view for a folder (used by the editor's initial state). */
export function consumePendingMiniappView(folderId: string): MiniAppView | undefined {
  const v = pending.get(folderId)
  pending.delete(folderId)
  return v
}

/**
 * Read (WITHOUT clearing) the pending view for a folder. Used by the workspace's
 * auto-open "land on run" default so it can defer to an explicit view already
 * requested from outside (e.g. the Drive/app-window wrench asking for 'settings').
 */
export function peekPendingMiniappView(folderId: string): MiniAppView | undefined {
  return pending.get(folderId)
}

/** Subscribe to view requests for an already-mounted editor. Returns an unsubscribe. */
export function onMiniappViewRequest(cb: (folderId: string, view: MiniAppView) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail as { folderId?: string; view?: MiniAppView } | undefined
    if (detail?.folderId && detail.view) cb(detail.folderId, detail.view)
  }
  window.addEventListener(EVENT, handler)
  return () => window.removeEventListener(EVENT, handler)
}

/**
 * "Run as app" signal — open the chromeless standalone app window for a
 * `.miniapp` folder (the `/app/:folderId` route). collab is deliberately
 * router-free, so the editor emits this and App.tsx (owner of the router +
 * tenant prefix) navigates on receipt.
 */
const OPEN_APP_EVENT = 'papan:miniapp-open-app'

/** Ask the shell to open `folderId` as a standalone app window. */
export function requestOpenAppWindow(folderId: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_APP_EVENT, { detail: { folderId } }))
  }
}

/** Subscribe to "run as app" requests (App.tsx). Returns an unsubscribe. */
export function onOpenAppWindowRequest(cb: (folderId: string) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail as { folderId?: string } | undefined
    if (detail?.folderId) cb(detail.folderId)
  }
  window.addEventListener(OPEN_APP_EVENT, handler)
  return () => window.removeEventListener(OPEN_APP_EVENT, handler)
}
